import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  Clock, 
  User, 
  MapPin, 
  Phone, 
  DollarSign,
  CheckCircle,
  AlertTriangle,
  XCircle,
  MoreHorizontal,
  CreditCard,
  Printer,
  Eye,
  Edit3,
  AlertCircle,
  RotateCcw,
  Trash2
} from 'lucide-react';
import { clsx } from 'clsx';
import { Booking } from '@/types';
import { toast } from 'react-hot-toast';
import { formatNZD } from '@/utils/currency';
import { hasBookingEndedNz } from '@/utils/bookingStatus';

interface BookingCardProps {
  booking: Booking;
  onQuickAction: (booking: Booking, action: string) => void;
  showConflictWarning?: boolean;
  isNew?: boolean;
  className?: string;
}

const BookingCard: React.FC<BookingCardProps> = ({
  booking,
  onQuickAction,
  showConflictWarning = false,
  isNew = false,
  className = ""
}) => {
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);

  const isPast = hasBookingEndedNz(booking);

  const canConfirm = booking.status === 'pending' && !isPast;
  const canConfirmAndPay =
    booking.booking_type === 'walk_in' &&
    booking.status === 'pending' &&
    booking.payment_status === 'pending' &&
    !isPast;
  const canPrint =
    booking.payment_status === 'paid' && (booking.status === 'confirmed' || booking.status === 'completed');
  const canComplete =
    isPast && (booking.status === 'confirmed' || booking.status === 'pending' || booking.status === 'expired');
  const canCancel = booking.status !== 'cancelled' && booking.status !== 'completed';
  const canRestore = booking.status === 'cancelled';
  const canDelete = booking.status === 'cancelled';
  const hasActions =
    canConfirm || canConfirmAndPay || canPrint || canComplete || canCancel || canRestore || canDelete;

  const statusConfig = {
    confirmed: {
      label: t('bookings.confirmed'),
      color: 'text-success-700 bg-success-50 border-success-200',
      icon: CheckCircle,
      dotColor: 'bg-success-500',
    },
    pending: {
      label: t('bookings.pending'),
      color: 'text-warning-700 bg-warning-50 border-warning-200',
      icon: AlertTriangle,
      dotColor: 'bg-warning-500',
    },
    cancelled: {
      label: t('bookings.cancelled'),
      color: 'text-red-700 bg-red-50 border-red-200',
      icon: XCircle,
      dotColor: 'bg-red-500',
    },
    completed: {
      label: t('bookings.completed'),
      color: 'text-primary-700 bg-primary-50 border-primary-200',
      icon: CheckCircle,
      dotColor: 'bg-primary-500',
    },
    expired: {
      label: t('bookings.expired'),
      color: 'text-gray-700 bg-gray-50 border-gray-200',
      icon: AlertTriangle,
      dotColor: 'bg-gray-500',
    },
  };

  const paymentStatusConfig = {
    paid: { label: t('bookings.paid'), color: 'text-success-700' },
    pending: { label: t('bookings.pendingPayment'), color: 'text-warning-700' },
    refunded: { label: t('bookings.refunded'), color: 'text-primary-700' },
    failed: { label: t('errors.generic'), color: 'text-red-700' },
  };

  const status = statusConfig[booking.status];
  const paymentStatus = paymentStatusConfig[booking.payment_status];
  const displayName =
    booking.customer_name ||
    booking.customer_display_name ||
    booking.customer_email ||
    booking.customer_email_reg ||
    '—';
  const displayPhone = booking.customer_phone || booking.customer_phone_reg || '';

  const handleQuickAction = async (action: string) => {
    setIsProcessing(true);
    try {
      await onQuickAction(booking, action);
    } catch (error) {
      toast.error(t('common.operationFailed'));
    } finally {
      setIsProcessing(false);
      setShowQuickActions(false);
    }
  };

  // 检测是否有时间冲突
  const hasTimeConflict = showConflictWarning && booking.status !== 'cancelled';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={clsx(
        "p-6 border rounded-xl bg-white transition-all duration-200 hover:shadow-medium relative",
        booking.status === 'cancelled' && "opacity-75",
        hasTimeConflict && "border-red-200 bg-red-50",
        isNew && "animate-glow-pulse border-l-4 border-l-success-500",
        className
      )}
    >
      {/* NEW 标签 */}
      {isNew && (
        <span className="absolute top-3 right-3 px-2 py-0.5 text-xs font-bold text-white bg-success-500 rounded-full shadow-sm">
          NEW
        </span>
      )}

      {/* 冲突警告 */}
      {hasTimeConflict && (
        <div className="flex items-center gap-2 mb-4 p-3 bg-red-100 rounded-lg">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <span className="text-sm text-red-800 font-medium">
            {t('bookings.timeConflictDetected')}
          </span>
        </div>
      )}

      {/* 预订头部信息 */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-500">
            #{booking.id.toString().padStart(6, '0')}
          </div>
          
          <span className={clsx(
            "px-3 py-1 rounded-full text-xs font-medium border",
            status.color
          )}>
            {status.label}
          </span>

          {isPast && booking.status !== 'completed' && booking.status !== 'expired' && (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
              {t('bookings.past')}
            </span>
          )}
          
          {booking.booking_type === 'walk_in' && (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
              {t('bookings.walkIn')}
            </span>
          )}

          {/* 实时库存指示器 */}
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-success-500 animate-pulse" />
            <span className="text-xs text-gray-500">{t('bookings.realTimeSync')}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-gray-900">
            {formatNZD(booking.total_price)}
          </span>
          
          {/* 快速操作按钮 */}
          <div className="relative">
            <button
              onClick={() => setShowQuickActions(!showQuickActions)}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              disabled={isProcessing}
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>

            {/* 快速操作菜单 */}
            {showQuickActions && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-hard border border-gray-200 p-2 min-w-48 z-10"
              >
                <div className="space-y-1">
                  {canConfirm && (
                    <button
                      onClick={() => handleQuickAction('confirm')}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium text-success-700 hover:bg-success-50 rounded-lg transition-colors"
                      disabled={isProcessing}
                    >
                      <CheckCircle className="h-4 w-4" />
                      {t('bookings.confirmBooking')}
                    </button>
                  )}

                  {canConfirmAndPay && (
                    <button
                      onClick={() => handleQuickAction('confirm_and_pay')}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium text-success-700 hover:bg-success-50 rounded-lg transition-colors"
                      disabled={isProcessing}
                    >
                      <CreditCard className="h-4 w-4" />
                      {t('bookings.confirmAndPay')}
                    </button>
                  )}

                  {canPrint && (
                    <button
                      onClick={() => handleQuickAction('print_receipt')}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                      disabled={isProcessing}
                    >
                      <Printer className="h-4 w-4" />
                      {t('bookings.printReceipt')}
                    </button>
                  )}

                  {canComplete && (
                    <button
                      onClick={() => handleQuickAction('complete')}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium text-purple-700 hover:bg-purple-50 rounded-lg transition-colors"
                      disabled={isProcessing}
                    >
                      <CheckCircle className="h-4 w-4" />
                      {t('bookings.completeBooking')}
                    </button>
                  )}

                  {canCancel && (
                    <button
                      onClick={() => handleQuickAction('cancel')}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                      disabled={isProcessing}
                    >
                      <XCircle className="h-4 w-4" />
                      {t('bookings.cancelBooking')}
                    </button>
                  )}

                  {canRestore && (
                    <button
                      onClick={() => handleQuickAction('restore')}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium text-success-700 hover:bg-success-50 rounded-lg transition-colors"
                      disabled={isProcessing}
                    >
                      <RotateCcw className="h-4 w-4" />
                      {t('bookings.restoreBooking')}
                    </button>
                  )}

                  {canDelete && (
                    <button
                      onClick={() => handleQuickAction('delete')}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                      disabled={isProcessing}
                    >
                      <Trash2 className="h-4 w-4" />
                      {t('bookings.deleteBooking')}
                    </button>
                  )}
                  {!hasActions && (
                    <div className="px-3 py-2 text-sm text-gray-400">
                      {t('common.none')}
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* 预订详情网格 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div className="flex items-center gap-3">
          <User className="h-4 w-4 text-gray-400" />
          <div>
            <div className="text-sm font-medium text-gray-900">
              {displayName}
            </div>
            <div className="text-xs text-gray-500">
              {booking.player_count} {t('createBooking.people')}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Clock className="h-4 w-4 text-gray-400" />
          <div>
            <div className="text-sm font-medium text-gray-900">
              {booking.start_time}-{booking.end_time}
            </div>
            <div className="text-xs text-gray-500">
              {booking.date}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <MapPin className="h-4 w-4 text-gray-400" />
          <div>
            <div className="text-sm font-medium text-gray-900">
              {booking.court_name}
            </div>
            <div className="text-xs text-gray-500">
              {booking.location_name}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <DollarSign className="h-4 w-4 text-gray-400" />
          <div>
            <div className="text-sm font-medium text-gray-900">
              {booking.payment_method === 'online' ? t('payment.onlinePayment') : 
               booking.payment_method === 'cash' ? t('payment.cash') :
               booking.payment_method === 'card' ? t('bookings.card') : t('bookings.transfer')}
            </div>
            <div className={clsx("text-xs font-medium", paymentStatus.color)}>
              {paymentStatus.label}
            </div>
          </div>
        </div>
      </div>

      {/* 联系方式 */}
      {(displayPhone || booking.customer_email || booking.customer_email_reg) && (
        <div className="flex items-center gap-3 mb-4 text-sm text-gray-600">
          <Phone className="h-4 w-4" />
          <span>{displayPhone || booking.customer_email || booking.customer_email_reg}</span>
          {displayPhone && (
            <button 
              onClick={() => navigator.clipboard.writeText(displayPhone)}
              className="text-xs text-primary-600 hover:text-primary-800 ml-2"
            >
              {t('common.copy')}
            </button>
          )}
        </div>
      )}

      {/* 特殊要求 */}
      {booking.special_requests && (
        <div className="p-3 bg-primary-50 rounded-lg mb-4">
          <p className="text-sm text-primary-800">
            <span className="font-medium">{t('createBooking.specialRequests')}:</span>
            {booking.special_requests}
          </p>
        </div>
      )}

      {/* 取消原因 */}
      {booking.cancellation_reason && (
        <div className="p-3 bg-red-50 rounded-lg mb-4">
          <p className="text-sm text-red-800">
            <span className="font-medium">{t('bookings.cancellationReason')}:</span>
            {booking.cancellation_reason}
          </p>
        </div>
      )}

      {/* 基础操作按钮 */}
      <div className="flex items-center gap-2 pt-4 border-t border-gray-100">
        <button
          onClick={() => onQuickAction(booking, 'view')}
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
        >
          <Eye className="h-4 w-4" />
          {t('common.viewDetails')}
        </button>

        {booking.status !== 'completed' && booking.status !== 'cancelled' && (
          <button
            onClick={() => onQuickAction(booking, 'edit')}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
          >
            <Edit3 className="h-4 w-4" />
            {t('common.edit')}
          </button>
        )}

        {/* 处理中状态指示器 */}
        {isProcessing && (
          <div className="flex items-center gap-2 px-3 py-1.5">
            <div className="w-4 h-4 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
            <span className="text-sm text-gray-600">{t('common.processing')}</span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default BookingCard;
